import { IApplicationState } from '@bees-web/core/types'
import { Dispatch } from '@reduxjs/toolkit'
import { useMemo, useCallback, useRef, useEffect } from 'react'

import { InteractivesBuilder } from '@/core/types'
import { isBrowser } from '@/core/utils/environment'

const MAX_DEFERRED_FLUSH_ROUNDS = 3

export const useInteractiveMap = (
  createInteractiveMap: InteractivesBuilder,
  dispatch: Dispatch,
  workspaceState: IApplicationState
) => {
  const lastStateRef = useRef<IApplicationState | null>(null)
  const lastMapRef = useRef<Record<string, unknown> | null>(null)
  const isCreatingMapRef = useRef(false)
  const pendingActionsRef = useRef<unknown[]>([])
  const flushCountRef = useRef(0)

  // Wraps dispatch to queue actions that happen during map creation (render phase).
  // Closures like `onClick: () => safeDispatch(action)` work normally because
  // isCreatingMapRef is false when the user actually clicks.
  const safeDispatch = useCallback(
    (action: unknown) => {
      if (isCreatingMapRef.current) {
        pendingActionsRef.current.push(action)
        return action
      }
      return dispatch(action as never)
    },
    [dispatch]
  )

  // Flush queued initialization dispatches after render, with circuit breaker
  useEffect(() => {
    const pending = pendingActionsRef.current
    if (pending.length === 0) return

    pendingActionsRef.current = []

    if (flushCountRef.current >= MAX_DEFERRED_FLUSH_ROUNDS) {
      console.warn(
        `[useInteractiveMap] Max deferred rounds (${MAX_DEFERRED_FLUSH_ROUNDS}) reached, dropping ${pending.length} actions to prevent infinite loop`
      )
      return
    }

    flushCountRef.current++
    pending.forEach((action) => dispatch(action as never))
  })

  // Reset circuit breaker when the workspace configuration changes
  useEffect(() => {
    flushCountRef.current = 0
  }, [createInteractiveMap])

  const stateChanged = useCallback((newState: IApplicationState) => {
    const lastState = lastStateRef.current
    if (!lastState) return true

    const keys = Object.keys(newState) as Array<keyof IApplicationState>
    // eslint-disable-next-line security/detect-object-injection
    return keys.some((key) => newState[key] !== lastState[key])
  }, [])

  return useMemo(() => {
    if (!isBrowser) return {}

    if (!stateChanged(workspaceState) && lastMapRef.current) {
      return lastMapRef.current
    }

    isCreatingMapRef.current = true
    const newMap = createInteractiveMap(
      safeDispatch as unknown as Dispatch,
      workspaceState
    )
    isCreatingMapRef.current = false

    lastStateRef.current = workspaceState
    lastMapRef.current = newMap

    return newMap
  }, [createInteractiveMap, safeDispatch, workspaceState, stateChanged])
}

export default useInteractiveMap
